import React, { useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./GamesPage.scss";
import GamesTabContent from "../../components/games-tab/GamesTabContent/GamesTabContent";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import { ROUTES, usePlayGameMetaDescription } from "../../utils/constants";
import useGameDetailsBySlugQuery from "../../react-query/queries/use-game-details-by-slug";
import { getCurrentOrigin, removeTrailingSlash } from "../../utils/helpers";

const HOMEPAGE_ROUTE = "/";

const GAMES_FEED_SEO: Record<
  string,
  { titleKey: string; descriptionKey: string; ogTitleKey: string }
> = {
  [ROUTES.ROOT.HOME]: {
    titleKey: "win_cash_in_1v1_and_multiplayer_games_hyper_gaming_app",
    descriptionKey: "home_seo_description",
    ogTitleKey: "win_cash_in_1v1_and_multiplayer_games",
  },
  [ROUTES.GAMES.ROOT]: {
    titleKey: "win_cash_in_1v1_and_multiplayer_games_hyper_gaming_app",
    descriptionKey: "games_seo_description",
    ogTitleKey: "win_cash_in_1v1_and_multiplayer_games",
  },
  [ROUTES.GAMES.PENDING]: {
    titleKey: "pending_games_seo_title",
    descriptionKey: "pending_games_seo_description",
    ogTitleKey: "win_cash_in_1v1_and_multiplayer_games",
  },
  [ROUTES.GAMES.GAME_REQUESTS]: {
    titleKey: "game_requests_seo_title",
    descriptionKey: "game_requests_seo_description",
    ogTitleKey: "win_cash_in_1v1_and_multiplayer_games",
  },
  [ROUTES.GAMES.LIVE_GAMES]: {
    titleKey: "live_games_seo_title",
    descriptionKey: "live_games_seo_description",
    ogTitleKey: "win_cash_in_1v1_and_multiplayer_games",
  },
  [ROUTES.GAMES.FAVORITES]: {
    titleKey: "favorite_games_seo_title",
    descriptionKey: "favorite_games_seo_description",
    ogTitleKey: "win_cash_in_1v1_and_multiplayer_games",
  },
};

function GamesPage() {
  const { slug } = useParams();
  const [queryParams] = useSearchParams();
  const gameQuery = queryParams.get("g");
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const metaDescriptions = usePlayGameMetaDescription();
  const { data: gameDataFromSlug } = useGameDetailsBySlugQuery({
    slug: slug ?? "",
  });
  const feedSeo = GAMES_FEED_SEO[removeTrailingSlash(pathname) || HOMEPAGE_ROUTE];

  const pageTitles = useMemo(() => {
    if (gameDataFromSlug) {
      const title = t(
        "metadata:game_name_available_for_play_on_web_and_mobile_hyper_gaming_app",
        { gameName: gameDataFromSlug.name }
      );
      return { title, ogTitle: extractOgTitleFromMainTitle(title) };
    }

    if (feedSeo) {
      return {
        title: t(`metadata:${feedSeo.titleKey}`),
        ogTitle: t(`metadata:${feedSeo.ogTitleKey}`),
      };
    }

    const title = t(
      "metadata:hundreds_of_free_online_games_with_zero_ads_hyper_gaming_app"
    );
    return { title, ogTitle: extractOgTitleFromMainTitle(title) };
  }, [t, gameDataFromSlug, feedSeo]);

  const description = useMemo(() => {
    if (feedSeo) {
      return t(`metadata:${feedSeo.descriptionKey}`);
    }
    if (pathname === HOMEPAGE_ROUTE) {
      return metaDescriptions.generic;
    }
    return metaDescriptions.generatePlayGameDescription(gameDataFromSlug?.name);
  }, [pathname, metaDescriptions, gameDataFromSlug, feedSeo, t]);

  const canonicalUrl = useMemo(() => {
    const origin = getCurrentOrigin();
    return gameQuery ? `${origin}/games/${gameQuery}` : `${origin}${pathname}`;
  }, [gameQuery, pathname]);

  const pageHeaders = renderHeaders({
    title: pageTitles.title,
    description,
    ogTitle: pageTitles.ogTitle,
    image: gameDataFromSlug?.poster,
    canonicalUrl,
  });

  return (
    <>
      {pageHeaders}
      <GamesTabContent />
    </>
  );
}

export default GamesPage;
